import { AxiosResponse } from "axios";
import { AiResponse } from "../types/airesponse";
import { ApiResponse } from "../types/apiresponse";
import { ImageRequest } from "../types/imagerequest";
import { PageResponse } from "../types/pageresponse";
import imagesService from "./imageRequestService";
import { ApiUsersResponse } from "../types/apiusersresponse";
import {InviteUserResponse} from "../types/inviteuserresponse";
import siteService, { Site, SiteDTO } from "./siteService";
import { AdminNotification } from '../types/adminnotification';


const apiInvocation = {
  /**
   * This method is used for fetching image requests from API and parsing them into types 
   * that we need for rendering. This endpoint returns pages of 
   * @param useAuth set to true if endpoint is secured, otherwise false
   * @param pageNumber param used for pagination
   * @param pageSize param used for pagination
   * @returns {@link PageResponse}
   */
  async getAllImagesPaginated(
    useAuth: boolean,
    pageNumber: number,
    pageSize: number,
    dateFrom: string | null,
    dateTo: string | null
  ): Promise<PageResponse> {
    const response = await imagesService.getAllImagesPaginated(
      useAuth,
      pageNumber,
      pageSize,
      dateFrom,
      dateTo
    );

    const mappedItems: ImageRequest[] = response.items.map((item: any) => {
      const parsedAIResponse = item.aiResponse
        ? (() => {
          const pascalResponse = JSON.parse(item.aiResponse);
          return {
            name: pascalResponse.Name,
            latinName: pascalResponse.LatinName,
            isPlantRecognized: pascalResponse.IsPlantRecognized,
            isAnimalRecognized: pascalResponse.IsAnimalRecognized,
            isEndangered: pascalResponse.IsEndangered,
            description: pascalResponse.Description,
            hasEnlistedEndangeredSpacies: pascalResponse.HasEnlistedEndangeredSpacies,
            overridenName: pascalResponse.OverridenName,
            isInvasive: pascalResponse.IsInvasive,
          };
        })()
        : undefined;

      return {
        id: item.id,
        imageLocation: item.imageLocation,
        aiResponse: parsedAIResponse,
        createdAt: item.createdAt,
        creaetdByUser: item.createdByUser,
        longitude: item.longitude,
        latitude: item.latitude,
        rating: item.rating,
        ratedByUser: item.ratedByUser ?? null,
        ratedAt: item.ratedAt ?? null,
        contentType: item.contentType,
        biodiversitySite: item.biodiversitySite,
      } as ImageRequest;
    });

    const parsedResponse: PageResponse = {
      items: mappedItems,
      totalCount: response.totalCount,
      pageNumber: response.pageNumber,
      pageSize: response.pageSize,
      totalPages: response.totalPages,
    };

    return parsedResponse;
  },

  /**
   * This method is used for uploading image and sending it to endpoint for image
   * recognition. That endpoint will return response from Azure OpenAI. 
   * 
   * @param image Image that we send to API
   * @param longitude we use this for better recognition with geolocation API
   * @param latitude we use this for better recognition with geolocation API
   * @param useAuth set to true if endpoint is secured, otherwise false
   * @returns {@link ImageRequest}
   */
  async uploadPhoto(
    image: File,
    longitude: number,
    latitude: number,
    useAuth: boolean,
    siteName: string,
  ): Promise<ImageRequest> {
    try {
      const response: ApiResponse = await imagesService.uploadPhoto(
        image,
        longitude,
        latitude,
        useAuth,
        siteName
      );

      const parsedAIResponse: AiResponse | undefined = response.aiResponse
        ? (() => {
          const pascalResponse = JSON.parse(response.aiResponse);
          return {
            name: pascalResponse.Name,
            latinName: pascalResponse.LatinName,
            isPlantRecognized: pascalResponse.IsPlantRecognized,
            isAnimalRecognized: pascalResponse.IsAnimalRecognized,
            isEndangered: pascalResponse.IsEndangered,
            description: pascalResponse.Description,
            hasEnlistedEndangeredSpacies: pascalResponse.HasEnlistedEndangeredSpacies,
            overridenName: pascalResponse.OverridenName,
            isInvasive: pascalResponse.IsInvasive,
          } as AiResponse;
        })()
        : undefined;

      const parsedResult: ImageRequest = {
        id: response.id,
        imageLocation: response.imageLocation,
        aiResponse: parsedAIResponse,
        createdAt: response.createdAt,
        creaetdByUser: response.createdByUser,
        longitude: response.longitude,
        latitude: response.latitude,
        rating: response.rating,
        ratedByUser: response.ratedByUser,
        ratedAt: response.ratedAt,
        contentType: response.contentType,
        biodiversitySite: response.biodiversitySite,
      };

      return parsedResult;
    } catch (error) {
      console.error("Error parsing upload photo response:", error);
      throw new Error("Failed to parse upload photo response.");
    }
  },

  /**
   * Sets a rating for an image request.
   */
  async setRating(requestId: string, rating: number, useAuth: boolean): Promise<void> {
    try {
      await imagesService.setRating(requestId, rating, useAuth); 
    } catch (error) {
      console.error("Error setting rating:", error);
      throw new Error("Failed to set rating.");
    }
  },

  /**
   * This method is fetching three previous requests that current user had before,
   * ordered by created at. We also map response into ImageRequest list
   * @returns List of {@link ImageRequest}
   */
  async fetchPreviousRequests(): Promise<ImageRequest[]> {
    try {
      const responses: ApiResponse[] = await imagesService.getPreviousRequests();
  
      const parsedResults: ImageRequest[] = responses.map((response) => {
        const parsedAIResponse: AiResponse | undefined = response.aiResponse
          ? (() => {
            const pascalResponse = JSON.parse(response.aiResponse);
            return {
              name: pascalResponse.Name,
              latinName: pascalResponse.LatinName,
              isPlantRecognized: pascalResponse.IsPlantRecognized,
              isAnimalRecognized: pascalResponse.IsAnimalRecognized,
              isEndangered: pascalResponse.IsEndangered,
              description: pascalResponse.Description,
              hasEnlistedEndangeredSpacies: pascalResponse.HasEnlistedEndangeredSpacies,
              overridenName: pascalResponse.OverridenName,
              isInvasive: pascalResponse.IsInvasive,
            } as AiResponse;
          })()
          : undefined;
  
        return {
          id: response.id,
          imageLocation: response.imageLocation,
          aiResponse: parsedAIResponse,
          createdAt: response.createdAt,
          creaetdByUser: response.createdByUser,
          longitude: response.longitude,
          latitude: response.latitude,
          rating: response.rating,
          ratedByUser: response.ratedByUser,
          ratedAt: response.ratedAt,
          contentType: response.contentType,
          biodiversitySite: response.biodiversitySite,
        } as ImageRequest;
      });
  
      return parsedResults;
    } catch (error) {
      console.error("Error fetching previous requests:", error);
      throw new Error("Failed to fetch previous requests.");
    }
  },
  
  /**
   * This method is used for fetching user statistics.
   * It fetches data from the `/Users/user-stats` endpoint.
   * @returns {Promise<ApiUsersResponse>} List of user statistics.
   */
  async getUsersData(pageNumber: number, pageSize: number): Promise<ApiUsersResponse> {
    try {
      return await imagesService.getUsersData(pageNumber, pageSize);
    } catch (error) {
      console.error("Error with fetching user statistics:", error);
      throw error;
    }
  },

  /**
   * This method fetches a CSV file from the `/Users/get-csv` endpoint.
   * The file is returned as a Blob, which can be downloaded by the client.
   * @returns {Promise<Blob>} The CSV file as a Blob.
   */
  async getCsvFile(): Promise<Blob> {
    try {
      // Call the service method that returns a Blob
      const response: AxiosResponse<Blob> = await imagesService.getCsvFile();
  
      // Return the Blob data directly
      return response.data;
    } catch (error) {
      console.error("Error fetching CSV file from API invocation:", error);
      throw new Error("Failed to fetch CSV file.");
    }
  },

  /**
   * This method overrides name that AI returned as name of plant or animal.
   * @param requestId used for finding image request
   * @param userId used for updating which admin updated request
   * @param newName new name of animal or plant
   * @returns void
   */
  async overrideName(requestId: string, newName: string, hasEnlistedEndangeredSpacies: boolean): Promise<ApiUsersResponse> {
    try {
      return await imagesService.overrideImageName(requestId, newName, hasEnlistedEndangeredSpacies);
    } catch (error) {
      console.error("Error with overriding image name:", error);
      throw error;
    }
  },

  /**
   * This method overrides if it's selected from invasive species list.
   * @param requestId used for finding image request
   * @param userId used for updating which admin updated request
   * @param newName new name of animal or plant
   * @returns void
   */
  async overrideNameInvasive(requestId: string, newName: string, hasEnlistedEndangeredSpacies: boolean): Promise<ApiUsersResponse> {
    try {
      return await imagesService.overrideImageNameInvasive(requestId, newName, hasEnlistedEndangeredSpacies);
    } catch (error) {
      console.error("Error with overriding image name:", error);
      throw error;
    }
  },


  /**
   * This method deletes image request by provided id.
   *
   * @param requestId
   */
  async deleteRequest(requestId: string) {
    try {
      return await imagesService.deleteRequest(requestId);
    } catch (error) {
      console.error("Error with overriding image name:", error);
      throw error;
    }
  },

  /**
   * This method overrides threat status, if it's endangered it will be changed to not endangered
   * Also when this status is changed, hasEnlistedEndangeredSpecies is changed to false 
   * @param requestId used for finding ImageRequest
   * @returns void
   */
  async overrideStatus(requestId: string): Promise<ApiUsersResponse> {
    try {
      return await imagesService.overrideThreatStatus(requestId);
    } catch (error) {
      console.error("Error with overriding image name:", error);
      throw error;
    }
  },

  /**
   * This method is used for sending invite to new user
   * @param newEmail
   */
  async inviteNewUser(newEmail: string): Promise<InviteUserResponse> {
    try {
      return await imagesService.inviteNewUser(newEmail);
    } catch (error) {
      console.error("Error with overriding image name:", error);
      throw error;
    }
  },

  async getAdminNotifications(): Promise<AdminNotification[]> {
    try {
      const data = await imagesService.getAdminNotifications();
      return data.map((item: any) => ({
        userId: item.userId,
        userName: item.userName,
        email: item.email,
        notificationsEnabled: item.notificationsEnabled,
      }));
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
      throw error;
    }
  },

  async updateAdminNotification(userId: string, userName: string, email: string, notificationsEnabled: boolean): Promise<void> {
    try {
      await imagesService.updateAdminNotification(userId, userName, email, notificationsEnabled);
    } catch (error) {
      console.error('Error updating admin notification:', error);
      throw error;
    }
  },

  async disableAllAdminNotifications(): Promise<void> {
    try {
      await imagesService.disableAllAdminNotifications();
    } catch (error) {
      console.error('Error disabling all admin notifications:', error);
      throw error;
    }
  },

  /**
   * Fetches all biodiversity sites from the database.
   * @returns List of {@link Site}
   */
  async getAllSites(): Promise<Site[]> {
    try {
      return await siteService.getAllSites();
    } catch (error) {
      console.error("Error fetching sites:", error);
      throw error;
    }
  },

  /**
   * Creates a new biodiversity site.
   * @param dto Site data
   * @returns Created {@link Site}
   */
  async createSite(dto: SiteDTO): Promise<Site> {
    try {
      return await siteService.createSite(dto);
    } catch (error) {
      console.error("Error creating site:", error);
      throw error;
    }
  },

  /**
   * Updates an existing biodiversity site.
   * @param id Site ID
   * @param dto Updated site data
   * @returns Updated {@link Site}
   */
  async updateSite(id: number, dto: SiteDTO): Promise<Site> {
    try {
      return await siteService.updateSite(id, dto);
    } catch (error) {
      console.error("Error updating site:", error);
      throw error;
    }
  },

  /**
   * Deletes a biodiversity site by ID.
   * @param id Site ID
   */
  async deleteSite(id: number): Promise<void> {
    try {
      await siteService.deleteSite(id);
    } catch (error) {
      console.error("Error deleting site:", error);
      throw error;
    }
  },

  /**
   * Imports sites from a CSV file (Name, Latitude, Longitude columns).
   * @param file CSV file to upload
   * @returns Import result with count
   */
  async importSitesCsv(file: File): Promise<{ imported: number; message: string }> {
    try {
      return await siteService.importCsv(file);
    } catch (error) {
      console.error("Error importing sites CSV:", error);
      throw error;
    }
  },
}

export default apiInvocation;